import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import FileInput from './FileInput';
import ReactSelect from 'react-select';
import ReactAsyncSelect from 'react-select/async';
import request from '../app/store/request';
import { generateOptions } from '../utils';

export const Input = props => {
  let {
    label,
    type,
    name,
    control,
    rules,
    error,
    info,
    placeholder = '',
    className = '',
    labelPlacement = 'right',
    dir = 'rtl',
    ...otherProps
  } = props;

  const AS_VALUES = ['textarea', 'select'];
  let conditionalProps = {
    ...(AS_VALUES.includes(type) && { as: type }),
    ...(!AS_VALUES.includes(type) && { type }),
  };

  return (
    <Form.Group className={'mb-1'}>
      {label && (
        <Form.Label className={`w-100 ${labelPlacement === 'right' ? 'text-left' : 'text-right'}`}>
          {label}
          {rules?.required && <span className={'pr-1'}>*</span>}
        </Form.Label>
      )}

      <Controller
        control={control}
        name={name}
        rules={rules}
        as={
          <Form.Control
            {...conditionalProps}
            placeholder={placeholder}
            isInvalid={!!error}
            className={`${type === 'textarea' ? 'custom-textArea' : 'custom-input'} ${className}`}
            style={{ direction: dir === 'rtl' ? 'rtl' : 'ltr' }}
            {...otherProps}
          />
        }
      />
      {info && <Form.Text className={'text-muted'}>{info}</Form.Text>}
      {error && (
        <Form.Control.Feedback type={'invalid'} className={'d-block'}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

Input.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  placeholder: PropTypes.string,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  control: PropTypes.string.isRequired,
  rules: PropTypes.object,
  className: PropTypes.string,
  error: PropTypes.string,
  info: PropTypes.string,
  labelPlacement: PropTypes.oneOf(['right', 'left']),
  dir: PropTypes.oneOf(['rtl', 'ltr']),
};
/*####################################################################################################################*/

export function InputFile(props) {
  let {
    label,
    rules,
    name,
    control,
    fileType,
    limitSize,
    error,
    info,
    imageHeight = 200,
    imageWidth = 200,
    onUpload,
    minWidth,
    minHeight,
    ...otherProps
  } = props;

  let conditionalProps = {
    ...(fileType === 'image' && { aeSize: { width: imageWidth, height: imageHeight } }),
  };

  return (
    <Form.Group>
      {label && (
        <Form.Label>
          {label}
          {rules?.required && <span className={'pr-1'}>*</span>}
        </Form.Label>
      )}
      <Controller
        name={name}
        type="file"
        as={FileInput}
        rules={rules}
        control={control}
        data-name={name}
        data-type={fileType}
        limitSize={limitSize}
        onChange={([event]) => event}
        uploadFile={onUpload}
        accept={`${fileType}/*`}
        label={label}
        minWidth={minWidth}
        minHeight={minHeight}
        classNames={{
          container: 'custom-file',
          input: 'custom-file-input',
          label: `${error ? 'border border-danger' : ''} custom-file-label`,
        }}
        {...conditionalProps}
        {...otherProps}
      />
      {info && <Form.Text className={'text-muted'}>{info}</Form.Text>}
      {error && (
        <Form.Control.Feedback type={'invalid'} className={'d-block'}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
}

InputFile.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  rules: PropTypes.object,
  name: PropTypes.string.isRequired,
  control: PropTypes.string.isRequired,
  fileType: PropTypes.oneOf(['image', 'video']).isRequired,
  limitSize: PropTypes.number.isRequired,
  error: PropTypes.string,
  info: PropTypes.string,
  imageHeight: PropTypes.number,
  imageWidth: PropTypes.number,
  onUpload: PropTypes.func,
};

/*####################################################################################################################*/
export function Select(props) {
  let {
    control,
    name,
    options = [],
    rules = {},
    defaultValue,
    label = '',
    placeholder = '',
    info = '',
    error = '',
    isMulti = false,
    ...otherProps
  } = props;

  let selectStyles = {
    control: currentStyles => {
      return {
        ...currentStyles,
        borderColor: error ? '#fd397a' : 'hsl(0,0%,80%)',
        boxShadow: 'unset',
        '&:hover': {
          borderColor: error ? '#fd397a' : 'hsl(0,0%,80%)',
        },
      };
    },
  };

  return (
    <Form.Group>
      {label && (
        <Form.Label>
          {label}
          {rules?.required && <span className={'pr-1'}>*</span>}
        </Form.Label>
      )}
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={defaultValue}
        as={
          <ReactSelect
            options={options}
            label={label}
            placeholder={placeholder}
            isMulti={isMulti}
            styles={selectStyles}
            {...otherProps}
          />
        }
        onChange={([selected]) => {
          return selected;
        }}
      />
      {info && <Form.Text className={'text-muted'}>{info}</Form.Text>}
      {error && (
        <Form.Control.Feedback type={'invalid'} className={'d-block'}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
}

Select.propTypes = {
  control: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  rules: PropTypes.object,
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  info: PropTypes.string,
  error: PropTypes.string,
  isMulti: PropTypes.bool,
};

/*####################################################################################################################*/
export function AsyncSelect(props) {
  let {
    control,
    name,
    options = [],
    rules = {},
    defaultValue,
    label = '',
    placeholder = '',
    info = '',
    error = '',
    isMulti = false,
    onInputChange,
    apiPath,
    queryParam,
    optionPattern,
    customLabel,
    ...otherProps
  } = props;

  let selectStyles = {
    control: currentStyles => {
      return {
        ...currentStyles,
        borderColor: error ? '#fd397a' : 'hsl(0,0%,80%)',
        boxShadow: 'unset',
        '&:hover': {
          borderColor: error ? '#fd397a' : 'hsl(0,0%,80%)',
        },
      };
    },
  };

  function handleInputChange(value) {
    onInputChange && onInputChange(value);
    return value;
  }

  function loadOptions(inputValue, callback) {
    request.get(apiPath, { params: { [queryParam]: inputValue } }).then(res => {
      let options = generateOptions(res.data.results, { ...optionPattern, customLabel: customLabel });
      callback(options);
    });
  }

  return (
    <Form.Group>
      {label && (
        <Form.Label>
          {label}
          {rules?.required && <span className={'pr-1'}>*</span>}
        </Form.Label>
      )}
      <Controller
        control={control}
        name={name}
        rules={rules}
        defaultValue={defaultValue}
        as={
          <ReactAsyncSelect
            maxMenuHeight={200}
            isClearable
            cacheOptions
            defaultOptions
            onInputChange={handleInputChange}
            loadOptions={loadOptions}
            label={label}
            placeholder={placeholder}
            isMulti={isMulti}
            styles={selectStyles}
            noOptionsMessage={() => 'موردی یافت نشد.'}
            loadingMessage={() => 'در حال جستجو...'}
            {...otherProps}
          />
        }
        onChange={([selected]) => {
          return selected;
        }}
      />
      {info && <Form.Text className={'text-muted'}>{info}</Form.Text>}
      {error && (
        <Form.Control.Feedback type={'invalid'} className={'d-block'}>
          {error}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
}

AsyncSelect.propTypes = {
  control: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  rules: PropTypes.object,
  defaultValue: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  label: PropTypes.string,
  placeholder: PropTypes.string,
  info: PropTypes.string,
  error: PropTypes.string,
  isMulti: PropTypes.bool,
  onInputChange: PropTypes.func.isRequired,
  apiPath: PropTypes.string.isRequired,
  queryParam: PropTypes.string.isRequired,
  optionPattern: PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }).isRequired,
  customLabel: PropTypes.func,
};
